import { motion, useMotionValue, useTransform } from "framer-motion";
import {
    BellRing,
    CalendarClock,
    CalendarPlus,
    CheckCircle2,
    Clock,
    Pencil,
    Plus,
    Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const bucketFor = (days) => {
    if (days == null) return { cls: "status-neutral", label: "No due" };
    if (days < 7) return { cls: "status-red", label: "Urgent" };
    if (days < 30) return { cls: "status-amber", label: "Soon" };
    return { cls: "status-green", label: "OK" };
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

const fmtAppt = (iso, tbc = false) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const date = d.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
        if (tbc) return `${date} · TBC`;
        const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
        return `${date} · ${time}`;
    } catch {
        return iso;
    }
};

const toInputValue = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
        return "";
    }
};

const toDateInputValue = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    } catch {
        return "";
    }
};

const toTimeInputValue = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const h = d.getHours();
        const m = d.getMinutes();
        if (h === 0 && m === 0) return ""; // treat 00:00 as "TBC"
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(h)}:${pad(m)}`;
    } catch {
        return "";
    }
};

const JobCard = ({ job, serviceName, onComplete, onDelete, onChange }) => {
    const x = useMotionValue(0);
    const [busy, setBusy] = useState(false);
    const [apptOpen, setApptOpen] = useState(false);
    const [apptDate, setApptDate] = useState(toDateInputValue(job.appointment_date));
    const [apptTime, setApptTime] = useState(toTimeInputValue(job.appointment_date));
    const [timeTbc, setTimeTbc] = useState(!!job.appointment_time_tbc);
    const [saving, setSaving] = useState(false);

    // Edit-job dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editScheduled, setEditScheduled] = useState((job.scheduled_date || "").slice(0, 10));
    const [editNotes, setEditNotes] = useState(job.notes || "");
    const [editServiceId, setEditServiceId] = useState(job.service_type_id || "");
    const [editPrice, setEditPrice] = useState(
        job.service_price_pence != null ? String(job.service_price_pence / 100) : "",
    );
    const [serviceTypes, setServiceTypes] = useState([]);
    const [lineItems, setLineItems] = useState(job.line_items || []);
    const [newItemName, setNewItemName] = useState("");
    const [newItemQty, setNewItemQty] = useState("1");
    const [newItemPrice, setNewItemPrice] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);

    useEffect(() => {
        if (editOpen && serviceTypes.length === 0) {
            api.get("/service-types")
                .then((r) => setServiceTypes(r.data || []))
                .catch(() => {});
        }
    }, [editOpen, serviceTypes.length]);

    const addLineItem = async () => {
        const price = parseFloat(newItemPrice);
        const qty = parseFloat(newItemQty) || 1;
        if (!newItemName.trim()) return toast.error("Line item name");
        if (!price || price < 0) return toast.error("Enter a price");
        try {
            const r = await api.post(`/jobs/${job.id}/line-items`, {
                name: newItemName.trim(),
                quantity: qty,
                unit_price_gbp: price,
            });
            setLineItems((prev) => [...prev, r.data]);
            setNewItemName("");
            setNewItemQty("1");
            setNewItemPrice("");
        } catch {
            toast.error("Add failed");
        }
    };

    const removeLineItem = async (itemId) => {
        try {
            await api.delete(`/jobs/${job.id}/line-items/${itemId}`);
            setLineItems((prev) => prev.filter((i) => i.id !== itemId));
        } catch {
            toast.error("Delete failed");
        }
    };

    const saveJob = async (e) => {
        e?.preventDefault?.();
        setSavingEdit(true);
        try {
            const payload = {
                scheduled_date: editScheduled,
                notes: editNotes,
            };
            if (editServiceId && editServiceId !== job.service_type_id) {
                payload.service_type_id = editServiceId;
            }
            if (editPrice !== "") {
                const p = parseFloat(editPrice);
                if (Number.isFinite(p) && p >= 0) payload.service_price = p;
            }
            await api.put(`/jobs/${job.id}`, payload);
            toast.success("Job updated");
            setEditOpen(false);
            onChange?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSavingEdit(false);
        }
    };

    const notifyCustomer = async () => {
        try {
            const r = await api.post(`/jobs/${job.id}/send-confirmation`);
            if (r.data?.status === "sent") {
                toast.success("Confirmation email sent");
            } else if (r.data?.status === "muted") {
                toast.info("Muted — reminder guard is OFF and this email isn't on the test whitelist");
            } else {
                toast.success("Notification triggered");
            }
            onChange?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        }
    };
    const overlayOpacity = useTransform(x, [0, 120, 240], [0, 0.6, 1]);

    const daysUntil =
        job.next_due_date != null
            ? Math.round(
                  (new Date(job.next_due_date).getTime() - new Date().getTime()) /
                      (1000 * 60 * 60 * 24),
              )
            : null;
    const b = bucketFor(daysUntil);
    const done = !!job.completed_at && !!(job.next_due_date == null || daysUntil > 30);
    const hasAppt = !!job.appointment_date;
    const bookingRequested = !!job.booking_requested_at && !hasAppt;

    // Invoice dialog (Done → invoice)
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [invoiceLines, setInvoiceLines] = useState([]);
    const [invoiceNotes, setInvoiceNotes] = useState("");
    const [invoiceSaving, setInvoiceSaving] = useState(false);

    const buildDefaultInvoiceLines = () => {
        const primaryUnitPence =
            job.service_price_pence != null
                ? job.service_price_pence
                : null;
        const rows = [];
        rows.push({
            name: serviceName || "Service",
            quantity: 1,
            unit_price_gbp:
                primaryUnitPence != null
                    ? (primaryUnitPence / 100).toFixed(2)
                    : "",
        });
        for (const li of job.line_items || []) {
            rows.push({
                name: li.name,
                quantity: li.quantity ?? 1,
                unit_price_gbp: ((li.unit_price_pence || 0) / 100).toFixed(2),
            });
        }
        return rows;
    };

    const invoiceTotalPence = invoiceLines.reduce((n, r) => {
        const q = parseFloat(r.quantity) || 0;
        const p = parseFloat(r.unit_price_gbp) || 0;
        return n + Math.round(q * p * 100);
    }, 0);

    const doComplete = async () => {
        if (busy) return;
        setInvoiceLines(buildDefaultInvoiceLines());
        setInvoiceNotes("");
        setInvoiceOpen(true);
    };

    const updateLine = (idx, patch) => {
        setInvoiceLines((prev) =>
            prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
        );
    };
    const addLine = () => {
        setInvoiceLines((prev) => [
            ...prev,
            { name: "", quantity: 1, unit_price_gbp: "" },
        ]);
    };
    const removeLine = (idx) => {
        setInvoiceLines((prev) => prev.filter((_, i) => i !== idx));
    };

    const sendInvoice = async (e) => {
        e?.preventDefault?.();
        const cleaned = invoiceLines
            .map((r) => ({
                name: (r.name || "").trim(),
                quantity: parseFloat(r.quantity) || 0,
                unit_price_gbp: parseFloat(r.unit_price_gbp) || 0,
            }))
            .filter((r) => r.name && r.quantity > 0 && r.unit_price_gbp >= 0);
        if (cleaned.length === 0) {
            toast.error("Add at least one line item");
            return;
        }
        const total = cleaned.reduce(
            (n, r) => n + r.quantity * r.unit_price_gbp,
            0,
        );
        if (total <= 0) {
            toast.error("Invoice total must be greater than 0");
            return;
        }
        setInvoiceSaving(true);
        try {
            await api.post(`/jobs/${job.id}/send-invoice`, {
                line_items: cleaned,
                notes: invoiceNotes.trim() || null,
                mark_job_complete: true,
            });
            toast.success("Invoice sent · job marked complete");
            setInvoiceOpen(false);
            onChange?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        } finally {
            setInvoiceSaving(false);
        }
    };

    const skipInvoice = async () => {
        setInvoiceSaving(true);
        setBusy(true);
        try {
            await onComplete(job.id);
            setInvoiceOpen(false);
        } finally {
            setBusy(false);
            setInvoiceSaving(false);
        }
    };

    const saveAppointment = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        try {
            const value = apptDate ? (apptTime ? `${apptDate}T${apptTime}` : apptDate) : null;
            await api.put(`/jobs/${job.id}/appointment`, {
                appointment_date: value,
            });
            toast.success(value ? "Appointment saved" : "Appointment cleared");
            setApptOpen(false);
            onChange?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const clearAppointment = async () => {
        setSaving(true);
        try {
            await api.put(`/jobs/${job.id}/appointment`, { appointment_date: null });
            toast.success("Appointment cleared");
            setApptOpen(false);
            setApptDate("");
            setApptTime("");
            onChange?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="relative overflow-hidden dh-card" data-testid={`job-card-${job.id}`}>
            <motion.div
                aria-hidden
                className="absolute inset-0 flex items-center justify-start pl-6 pointer-events-none"
                style={{ background: "var(--dh-green)", opacity: overlayOpacity }}
            >
                <div className="flex items-center gap-2 text-white font-display font-black uppercase text-lg">
                    <CheckCircle2 /> Swipe to complete
                </div>
            </motion.div>

            <motion.div
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={{ left: 0, right: 0.7 }}
                style={{ x }}
                onDragEnd={(_, info) => {
                    if (info.offset.x > 140) doComplete();
                }}
                className="relative bg-white"
            >
                <div className="flex">
                    <div
                        className={`${b.cls} w-24 sm:w-28 p-4 flex flex-col justify-center items-start`}
                    >
                        <p className="text-[10px] tracking-widest uppercase opacity-80 font-bold">
                            {b.label}
                        </p>
                        <p className="font-display font-black text-3xl leading-none">
                            {daysUntil == null
                                ? "—"
                                : daysUntil < 0
                                ? `${Math.abs(daysUntil)}d`
                                : `${daysUntil}d`}
                        </p>
                        {daysUntil != null && (
                            <p className="text-[10px] opacity-80 mt-1">
                                {daysUntil < 0 ? "overdue" : "to go"}
                            </p>
                        )}
                    </div>
                    <div className="flex-1 p-4">
                        <p className="text-label">{serviceName || "Service"}</p>
                        <p className="font-display font-black uppercase text-lg leading-tight">
                            {fmtDate(job.scheduled_date)}
                        </p>
                        <p className="text-xs font-mono text-neutral-600 mt-1">
                            <Clock size={12} className="inline mr-1" />
                            Next due: {fmtDate(job.next_due_date)} · reminder{" "}
                            {job.reminder_sent ? "sent" : "pending"}
                        </p>

                        {bookingRequested && (
                            <div
                                className="mt-3 px-3 py-2 inline-flex items-center gap-2 border-2 border-[color:var(--dh-orange)] bg-orange-50"
                                data-testid={`job-booking-flag-${job.id}`}
                            >
                                <CalendarPlus size={14} className="text-[color:var(--dh-orange)]" />
                                <span className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-orange-hot)]">
                                    Customer requested a booking · {fmtDate(job.booking_requested_at)}
                                </span>
                            </div>
                        )}

                        {hasAppt && (
                            <div
                                className="mt-3 px-3 py-2 inline-flex items-center gap-2 status-green"
                                data-testid={`job-appointment-flag-${job.id}`}
                            >
                                <CalendarClock size={14} />
                                <span className="text-xs font-bold uppercase tracking-wider">
                                    Appointment · {fmtAppt(job.appointment_date, job.appointment_time_tbc)}
                                </span>
                            </div>
                        )}

                        {job.notes && (
                            <p className="text-sm text-neutral-700 mt-2 whitespace-pre-line">{job.notes}</p>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2">
                            <button
                                data-testid={`job-set-appt-${job.id}`}
                                onClick={() => {
                                    setApptDate(toDateInputValue(job.appointment_date));
                                    setApptTime(toTimeInputValue(job.appointment_date));
                                    setTimeTbc(!!job.appointment_time_tbc);
                                    setApptOpen(true);
                                }}
                                className="dh-btn"
                                data-variant={hasAppt ? "ghost" : undefined}
                            >
                                <CalendarClock size={16} />{" "}
                                {hasAppt ? "Reschedule" : "Set appointment"}
                            </button>
                            {hasAppt && (
                                <button
                                    data-testid={`job-notify-${job.id}`}
                                    onClick={notifyCustomer}
                                    className="dh-btn"
                                    title={
                                        job.appointment_confirmed_sent
                                            ? "Confirmation already sent — resend it"
                                            : "Send the customer a confirmation email"
                                    }
                                >
                                    <BellRing size={16} />{" "}
                                    {job.appointment_confirmed_sent
                                        ? "Resend notify"
                                        : "Notify"}
                                </button>
                            )}
                            <button
                                data-testid={`job-complete-${job.id}`}
                                onClick={doComplete}
                                className="dh-btn"
                                data-variant="ghost"
                                disabled={busy}
                            >
                                <CheckCircle2 size={16} /> {busy ? "Saving…" : "Mark complete"}
                            </button>
                            <button
                                data-testid={`job-edit-${job.id}`}
                                onClick={() => {
                                    setEditScheduled((job.scheduled_date || "").slice(0, 10));
                                    setEditNotes(job.notes || "");
                                    setEditServiceId(job.service_type_id || "");
                                    setEditPrice(
                                        job.service_price_pence != null
                                            ? String(job.service_price_pence / 100)
                                            : "",
                                    );
                                    setLineItems(job.line_items || []);
                                    setEditOpen(true);
                                }}
                                className="dh-btn"
                                data-variant="ghost"
                            >
                                <Pencil size={16} /> Edit
                            </button>
                            <button
                                data-testid={`job-delete-${job.id}`}
                                onClick={() => onDelete(job.id)}
                                className="dh-btn"
                                data-variant="ghost"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-tight text-2xl">
                            Edit job
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={saveJob} className="space-y-4" data-testid="job-edit-form">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-label block mb-1">Installation / scheduled date</label>
                                <input
                                    type="date"
                                    className="dh-input"
                                    data-testid="job-edit-date"
                                    value={editScheduled}
                                    onChange={(e) => setEditScheduled(e.target.value)}
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-label block mb-1">Primary service</label>
                                <Select value={editServiceId} onValueChange={setEditServiceId}>
                                    <SelectTrigger
                                        className="dh-input"
                                        data-testid="job-edit-service"
                                    >
                                        <SelectValue placeholder="Pick service" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {serviceTypes.map((s) => (
                                            <SelectItem key={s.id} value={s.id}>
                                                {s.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="text-label block mb-1">
                                Primary service price (£) — leave blank for default
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="dh-input"
                                data-testid="job-edit-price"
                                value={editPrice}
                                onChange={(e) => setEditPrice(e.target.value)}
                                placeholder="90.00"
                            />
                        </div>

                        {/* Extra line items */}
                        <div className="border-2 border-neutral-900 p-3">
                            <p className="text-[10px] uppercase tracking-widest font-black text-neutral-500 mb-2">
                                Extra services on this job
                            </p>
                            <div className="space-y-1" data-testid="job-line-items-list">
                                {lineItems.length === 0 && (
                                    <p className="text-xs text-neutral-500">
                                        None — add radiators, taps, extras below.
                                    </p>
                                )}
                                {lineItems.map((it) => (
                                    <div
                                        key={it.id}
                                        className="flex items-center gap-2 text-sm"
                                        data-testid={`job-line-item-${it.id}`}
                                    >
                                        <span className="flex-1 truncate">
                                            {it.quantity !== 1 ? `${it.quantity} × ` : ""}
                                            {it.name}
                                        </span>
                                        <span className="font-bold">
                                            £{((it.total_pence || 0) / 100).toFixed(2)}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeLineItem(it.id)}
                                            className="text-neutral-500 hover:text-red-600"
                                            aria-label="Remove"
                                            data-testid={`job-line-item-del-${it.id}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-2 grid grid-cols-[1fr_60px_80px_auto] gap-1 items-center">
                                <input
                                    type="text"
                                    value={newItemName}
                                    onChange={(e) => setNewItemName(e.target.value)}
                                    placeholder="Radiator / Bath tap / …"
                                    className="border-2 border-neutral-900 h-9 px-2 text-sm"
                                    data-testid="job-line-item-name"
                                />
                                <input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    value={newItemQty}
                                    onChange={(e) => setNewItemQty(e.target.value)}
                                    placeholder="Qty"
                                    className="border-2 border-neutral-900 h-9 px-2 text-sm text-center"
                                    data-testid="job-line-item-qty"
                                />
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={newItemPrice}
                                    onChange={(e) => setNewItemPrice(e.target.value)}
                                    placeholder="£ each"
                                    className="border-2 border-neutral-900 h-9 px-2 text-sm"
                                    data-testid="job-line-item-price"
                                />
                                <button
                                    type="button"
                                    onClick={addLineItem}
                                    className="dh-btn h-9 !px-2"
                                    aria-label="Add line item"
                                    data-testid="job-line-item-add"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="text-label block mb-1">Notes</label>
                            <textarea
                                className="dh-input"
                                data-testid="job-edit-notes"
                                rows={3}
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                placeholder="e.g. Model: ecoFIT pure 830 | Guarantee: 10 year conditional"
                            />
                            <p className="text-xs text-neutral-500 mt-1 font-mono">
                                Boiler model, warranty details, access notes — anything Hayden should know.
                            </p>
                        </div>
                        <DialogFooter>
                            <button
                                type="submit"
                                className="dh-btn"
                                disabled={savingEdit}
                                data-testid="job-edit-save"
                            >
                                {savingEdit ? "Saving…" : "Save changes"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={apptOpen} onOpenChange={setApptOpen}>
                <DialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-tight text-2xl">
                            {hasAppt ? "Reschedule appointment" : "Set appointment date"}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={saveAppointment} className="space-y-4" data-testid="appointment-form">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-label block mb-1">Date</label>
                                <input
                                    type="date"
                                    className="dh-input"
                                    data-testid="appointment-date-input"
                                    value={apptDate}
                                    onChange={(e) => setApptDate(e.target.value)}
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-label block mb-1">
                                    Time <span className="text-neutral-400 normal-case">(optional)</span>
                                </label>
                                <input
                                    type="time"
                                    className="dh-input"
                                    data-testid="appointment-time-input"
                                    value={apptTime}
                                    onChange={(e) => {
                                        setApptTime(e.target.value);
                                        if (e.target.value) setTimeTbc(false);
                                    }}
                                    disabled={timeTbc}
                                />
                                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        data-testid="appointment-tbc-checkbox"
                                        checked={timeTbc}
                                        onChange={(e) => {
                                            setTimeTbc(e.target.checked);
                                            if (e.target.checked) setApptTime("");
                                        }}
                                        className="h-4 w-4 accent-[color:var(--dh-orange)]"
                                    />
                                    <span className="text-xs uppercase tracking-widest font-bold">
                                        Time TBC
                                    </span>
                                </label>
                            </div>
                        </div>
                        <p className="text-xs text-neutral-500 font-mono">
                            We&apos;ll email the customer straight away to confirm, then again 3 days before
                            and the day before. All emails still respect the reminder-guard toggle.
                        </p>
                        <DialogFooter className="gap-2">
                            {hasAppt && (
                                <button
                                    type="button"
                                    className="dh-btn"
                                    data-variant="danger"
                                    onClick={clearAppointment}
                                    disabled={saving}
                                    data-testid="appointment-clear-btn"
                                >
                                    Clear
                                </button>
                            )}
                            <button
                                type="submit"
                                className="dh-btn"
                                disabled={saving || !apptDate}
                                data-testid="appointment-save-btn"
                            >
                                {saving ? "Saving…" : "Save appointment"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
                <DialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase">Send invoice &amp; complete job</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={sendInvoice} className="space-y-4" data-testid={`invoice-form-${job.id}`}>
                        <div className="border-2 border-neutral-900">
                            <div className="grid grid-cols-[1fr_60px_100px_100px_auto] gap-1 bg-neutral-900 text-white px-2 py-1 text-[10px] uppercase tracking-widest font-black">
                                <div>Description</div>
                                <div className="text-center">Qty</div>
                                <div className="text-right">Unit £</div>
                                <div className="text-right">Total</div>
                                <div></div>
                            </div>
                            {invoiceLines.map((r, idx) => {
                                const q = parseFloat(r.quantity) || 0;
                                const p = parseFloat(r.unit_price_gbp) || 0;
                                const total = q * p;
                                return (
                                    <div
                                        key={idx}
                                        className="grid grid-cols-[1fr_60px_100px_100px_auto] gap-1 items-center px-2 py-1 border-t border-neutral-200"
                                        data-testid={`invoice-line-${idx}`}
                                    >
                                        <input
                                            type="text"
                                            value={r.name}
                                            onChange={(e) => updateLine(idx, { name: e.target.value })}
                                            placeholder="Item"
                                            className="border border-neutral-300 h-8 px-2 text-sm"
                                            data-testid={`invoice-line-name-${idx}`}
                                        />
                                        <input
                                            type="number"
                                            step="0.5"
                                            min="0"
                                            value={r.quantity}
                                            onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                                            className="border border-neutral-300 h-8 px-1 text-sm text-center"
                                            data-testid={`invoice-line-qty-${idx}`}
                                        />
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={r.unit_price_gbp}
                                            onChange={(e) => updateLine(idx, { unit_price_gbp: e.target.value })}
                                            placeholder="0.00"
                                            className="border border-neutral-300 h-8 px-2 text-sm text-right"
                                            data-testid={`invoice-line-unit-${idx}`}
                                        />
                                        <div className="text-right text-sm font-bold">
                                            £{total.toFixed(2)}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeLine(idx)}
                                            className="text-neutral-500 hover:text-red-600"
                                            aria-label="Remove"
                                            data-testid={`invoice-line-del-${idx}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                );
                            })}
                            <div className="px-2 py-1 border-t border-neutral-200 flex justify-between items-center">
                                <button
                                    type="button"
                                    onClick={addLine}
                                    className="text-xs font-black uppercase tracking-wider text-orange-600 hover:text-orange-700"
                                    data-testid={`invoice-line-add-${job.id}`}
                                >
                                    <Plus size={12} className="inline" /> Add row
                                </button>
                                <div className="text-right">
                                    <span className="text-[10px] uppercase tracking-widest font-black text-neutral-500 mr-2">
                                        Total
                                    </span>
                                    <span
                                        className="font-display font-black text-2xl text-orange-600"
                                        data-testid={`invoice-total-${job.id}`}
                                    >
                                        £{(invoiceTotalPence / 100).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="text-label block mb-1">Notes (optional, shown on invoice)</label>
                            <textarea
                                rows={2}
                                className="dh-input"
                                data-testid={`invoice-notes-${job.id}`}
                                value={invoiceNotes}
                                onChange={(e) => setInvoiceNotes(e.target.value)}
                            />
                        </div>
                        <p className="text-xs text-neutral-500 leading-5">
                            The customer gets an email with the PDF attached and a link to view
                            online. The job is marked complete and its next annual date is
                            queued automatically.
                        </p>
                        <DialogFooter className="flex-wrap gap-2">
                            <button
                                type="button"
                                className="text-xs text-neutral-500 underline"
                                data-testid={`invoice-skip-${job.id}`}
                                onClick={skipInvoice}
                                disabled={invoiceSaving}
                            >
                                Skip invoice, just mark complete
                            </button>
                            <button
                                className="dh-btn"
                                data-testid={`invoice-send-${job.id}`}
                                disabled={invoiceSaving}
                            >
                                {invoiceSaving ? "Sending…" : "Send invoice"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default JobCard;
